import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Megaphone, Settings as SettingsIcon, Clock, LogOut, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { Brand } from "@/components/Nav";
import { Overview } from "@/components/host/Overview";
import { GuestsTab } from "@/components/host/GuestsTab";
import { AnnouncementsTab } from "@/components/host/AnnouncementsTab";
import { SettingsTab } from "@/components/host/SettingsTab";
import { WaitlistTab } from "@/components/host/WaitlistTab";
import { TextsTab } from "@/components/host/TextsTab";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "guests", label: "Guests", icon: Users },
  { id: "waitlist", label: "Waitlist", icon: Clock },
  { id: "texts", label: "Texts", icon: MessageSquare },
  { id: "announcements", label: "Announcements", icon: Megaphone },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

export default function HostDashboard() {
  const nav = useNavigate();
  const loc = useLocation();
  const [user, setUser] = useState(loc.state?.user || null);
  const [status, setStatus] = useState(loc.state?.user ? "ok" : "checking");
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (loc.state?.user) return;
    if (window.location.hash?.includes("session_id=")) return;
    api.get("/auth/me").then((r) => { setUser(r.data); setStatus("ok"); }).catch(() => { setStatus("no"); nav("/host/login"); });
  }, [nav, loc.state]);

  const logout = async () => { try { await api.post("/auth/logout"); } catch (_) {} nav("/host/login"); };

  if (status !== "ok") return <div className="min-h-screen bg-cream grid place-items-center"><div className="w-10 h-10 rounded-full border-2 border-gold border-t-transparent animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-cream2/50">
      <header className="sticky top-0 z-40 bg-cream/90 backdrop-blur-xl border-b border-hair">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brand />
            <span className="hidden sm:inline text-eyebrow text-inkMuted border-l border-hair pl-3">Host</span>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
            <span className="hidden sm:inline text-sm text-inkSoft">{user?.name}</span>
            <button onClick={logout} className="flex items-center gap-1.5 text-sm text-inkSoft hover:text-espresso" data-testid="host-logout"><LogOut size={16} /></button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-2 sm:px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`host-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition ${tab === t.id ? "border-gold text-espresso" : "border-transparent text-inkMuted hover:text-espresso"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {tab === "overview" && <Overview onManageGuests={() => setTab("guests")} />}
        {tab === "guests" && <GuestsTab />}
        {tab === "waitlist" && <WaitlistTab />}
        {tab === "texts" && <TextsTab />}
        {tab === "announcements" && <AnnouncementsTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}
